import React, { useState, useRef, useEffect } from 'react';
import '../styles/FloatingWindow.css';

const FloatingWindow = ({
  id,
  title,
  children,
  onClose,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 1000, height: 600 },
  zIndex,
  onFocus,
  isMobileFullScreen = false
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(isMobileFullScreen);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [preMaximizeState, setPreMaximizeState] = useState(null);

  const windowRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && !isMaximized) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }

      if (isResizing && resizeDirection) {
        const newSize = { ...size };
        const newPosition = { ...position };

        if (resizeDirection.includes('e')) {
          newSize.width = Math.max(400, e.clientX - position.x);
        }
        if (resizeDirection.includes('s')) {
          newSize.height = Math.max(300, e.clientY - position.y);
        }
        if (resizeDirection.includes('w')) {
          const newWidth = Math.max(400, position.x + size.width - e.clientX);
          const deltaWidth = size.width - newWidth;
          newSize.width = newWidth;
          newPosition.x = position.x + deltaWidth;
        }
        if (resizeDirection.includes('n')) {
          const newHeight = Math.max(300, position.y + size.height - e.clientY);
          const deltaHeight = size.height - newHeight;
          newSize.height = newHeight;
          newPosition.y = position.y + deltaHeight;
        }

        setSize(newSize);
        setPosition(newPosition);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeDirection(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, position, size, resizeDirection, isMaximized]);

  const handleMouseDown = (e) => {
    // Disable dragging on mobile full-screen mode
    if (isMobileFullScreen) return;

    if (e.target.closest('.window-header') && !e.target.closest('.window-controls')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      onFocus && onFocus(id);
    }
  };

  const handleResizeStart = (direction) => (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    onFocus && onFocus(id);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleMaximize = () => {
    if (isMaximized) {
      // Restore previous state
      if (preMaximizeState) {
        setPosition(preMaximizeState.position);
        setSize(preMaximizeState.size);
      }
      setIsMaximized(false);
    } else {
      // Save current state and maximize
      setPreMaximizeState({ position, size });
      setPosition({ x: 0, y: 0 });
      setSize({ width: window.innerWidth, height: window.innerHeight });
      setIsMaximized(true);
    }
  };

  const handleClose = () => {
    onClose && onClose(id);
  };

  const windowStyle = isMaximized || isMobileFullScreen
    ? { left: 0, top: 0, width: '100vw', height: '100vh', zIndex }
    : {
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: isMinimized ? 'auto' : `${size.height}px`,
        zIndex
      };

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobileFullScreen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: zIndex - 1
          }}
          onClick={handleClose}
        />
      )}

      <div
        ref={windowRef}
        className={`floating-window ${isMinimized ? 'minimized' : ''} ${isMaximized || isMobileFullScreen ? 'maximized' : ''} ${isMobileFullScreen ? 'mobile-fullscreen' : ''}`}
        style={windowStyle}
        onMouseDown={() => onFocus && onFocus(id)}
      >
        <div
          className="window-header"
          onMouseDown={handleMouseDown}
        >
          <div className="window-title">{title}</div>
          <div className="window-controls">
            {!isMobileFullScreen && (
              <>
                <button
                  className="window-control-btn minimize"
                  onClick={handleMinimize}
                  title="Minimize"
                >
                  −
                </button>
                <button
                  className="window-control-btn maximize"
                  onClick={handleMaximize}
                  title={isMaximized ? "Restore" : "Maximize"}
                >
                  {isMaximized ? '❐' : '□'}
                </button>
              </>
            )}
            <button
              className="window-control-btn close"
              onClick={handleClose}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>

        {!isMinimized && (
          <div className="window-content">
            {children}
          </div>
        )}

        {!isMinimized && !isMaximized && !isMobileFullScreen && (
          <>
            <div className="resize-handle n" onMouseDown={handleResizeStart('n')} />
            <div className="resize-handle s" onMouseDown={handleResizeStart('s')} />
            <div className="resize-handle e" onMouseDown={handleResizeStart('e')} />
            <div className="resize-handle w" onMouseDown={handleResizeStart('w')} />
            <div className="resize-handle ne" onMouseDown={handleResizeStart('ne')} />
            <div className="resize-handle nw" onMouseDown={handleResizeStart('nw')} />
            <div className="resize-handle se" onMouseDown={handleResizeStart('se')} />
            <div className="resize-handle sw" onMouseDown={handleResizeStart('sw')} />
          </>
        )}
      </div>
    </>
  );
};

export default FloatingWindow;
